import React, {
  useMemo,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import {
  ReactFlow,
  useReactFlow,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  Node,
  Background,
  Controls,
  MiniMap,
  MarkerType,
  Panel,
  EdgeChange,
  NodeChange,
  applyNodeChanges,
  applyEdgeChanges,
  BaseEdge,
  getBezierPath,
  EdgeProps,
  EdgeLabelRenderer,
  ReactFlowProvider,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Stack,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Save as SaveIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import useTheme from "../../../hooks/useTheme";
import { THEMES } from "../../../constants";

// Custom edge to handle curvature for multiple edges between same nodes
const CurvedEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  label,
  data,
}) => {
  const curvature = data?.curvature || 0;
  const onCurvatureChange = data?.onCurvatureChange;
  const { screenToFlowPosition } = useReactFlow();
  const { theme } = useTheme();
  const isDarkMode = theme === THEMES.DARK;

  // Calculate custom quadratic bezier path
  const mx = (sourceX + targetX) / 2;
  const my = (sourceY + targetY) / 2;
  const dx = targetX - sourceX;
  const dy = targetY - sourceY;

  // Control point for quadratic bezier
  const cx = mx + dy * curvature * -1;
  const cy = my + dx * curvature;

  const edgePath = `M ${sourceX},${sourceY} Q ${cx},${cy} ${targetX},${targetY}`;

  const labelX = (sourceX + 2 * cx + targetX) / 4;
  const labelY = (sourceY + 2 * cy + targetY) / 4;

  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = (e) => {
    e.stopPropagation();
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY });

      const lenSq = dx * dx + dy * dy;
      if (lenSq < 100) return;

      const vx = flowPos.x - mx;
      const vy = flowPos.y - my;

      const nx = -dy;
      const ny = dx;

      const dot = vx * nx + vy * ny;
      const newCurvature = (2 * dot) / lenSq;

      if (onCurvatureChange) {
        onCurvatureChange(id, newCurvature);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      if (data?.onCurvatureChangeEnd) {
        data.onCurvatureChangeEnd();
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    isDragging,
    id,
    onCurvatureChange,
    curvature,
    sourceX,
    sourceY,
    targetX,
    targetY,
    screenToFlowPosition,
    data?.onCurvatureChangeEnd,
    dx,
    dy,
    mx,
    my,
  ]);

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              background: isDarkMode ? "#1e293b" : "#fff",
              padding: "2px 6px",
              borderRadius: 4,
              fontSize: 10,
              fontWeight: 700,
              pointerEvents: "all",
              border: `1px solid ${isDarkMode ? "#60a5fa" : "#3b82f6"}`,
              color: isDarkMode ? "#bfdbfe" : "#1e40af",
              boxShadow: isDarkMode
                ? "0 1px 3px rgba(0,0,0,0.5)"
                : "0 1px 3px rgba(0,0,0,0.1)",
              cursor: isDragging ? "grabbing" : "grab",
              zIndex: 1000,
              userSelect: "none",
            }}
            onMouseDown={handleMouseDown}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

const edgeTypes = {
  curved: CurvedEdge,
};

const WorkflowGraphInner = ({ config, onUpdateConfig }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === THEMES.DARK;
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);

  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEdge, setEditingEdge] = useState(null);
  const [transitionData, setTransitionData] = useState({
    action: "",
    requiredRoles: [],
    validationRules: "",
    endpoint: "",
    curvature: 0.25,
  });

  // Sync with config
  const syncToConfig = useCallback(
    (currentEdges, currentNodes) => {
      const newStates = currentNodes.map((node, index) => {
        const existingState = config.states.find((s) => s.id === node.id);

        return {
          id: node.id,
          label: node.data.label || node.id,
          isFinal: node.type === "output" || existingState?.isFinal || false,
          order: existingState?.order || index + 1,
          position: node.position,
        };
      });

      const newTransitions = currentEdges.map((edge) => {
        const isEditing = editingEdge && edge.id === editingEdge.id;
        const transitionId = edge.data?.transitionId || edge.id;

        const existingTransition =
          config.transitions.find((t) => t.id === transitionId) ||
          config.transitions.find(
            (t) =>
              t.from === edge.source &&
              t.to === edge.target &&
              t.action === edge.label,
          );

        return {
          id: transitionId,
          from: edge.source,
          to: edge.target,
          action: isEditing ? transitionData.action : edge.label || "Azione",
          requiredRoles: isEditing
            ? transitionData.requiredRoles
            : existingTransition?.requiredRoles || [],
          validationRules: isEditing
            ? (transitionData.validationRules || "")
                .split(",")
                .map((r) => r.trim())
                .filter((r) => r)
            : existingTransition?.validationRules || [],
          endpoint: isEditing
            ? transitionData.endpoint
            : existingTransition?.endpoint || "",
          curvature: edge.data?.curvature || 0.25,
        };
      });

      onUpdateConfig({
        ...config,
        states: newStates,
        transitions: newTransitions,
      });
    },
    [config, editingEdge, transitionData, onUpdateConfig],
  );

  const onCurvatureChange = useCallback((edgeId, curvature) => {
    setEdges((eds) =>
      eds.map((edge) => {
        if (edge.id === edgeId) {
          return {
            ...edge,
            data: {
              ...edge.data,
              curvature: curvature,
            },
          };
        }
        return edge;
      }),
    );
  }, []);

  const onCurvatureChangeEnd = useCallback(() => {
    syncToConfig(edgesRef.current, nodesRef.current);
  }, [syncToConfig]);

  const onNodeDragStop = useCallback(() => {
    syncToConfig(edgesRef.current, nodesRef.current);
  }, [syncToConfig]);

  // Map states to nodes
  const initialNodes = useMemo(() => {
    return config.states.map((state, index) => ({
      id: state.id,
      data: { label: state.label },
      position: state.position || {
        x: 100 + (index % 3) * 250,
        y: 100 + Math.floor(index / 3) * 150,
      },
      type: state.isFinal ? "output" : index === 0 ? "input" : "default",
      style: {
        background: isDarkMode
          ? state.isFinal
            ? "#064e3b"
            : index === 0
            ? "#1e3a8a"
            : "#1e293b"
          : state.isFinal
          ? "#f0fdf4"
          : index === 0
          ? "#eff6ff"
          : "#fff",
        borderColor: isDarkMode
          ? state.isFinal
            ? "#10b981"
            : index === 0
            ? "#3b82f6"
            : "#475569"
          : state.isFinal
          ? "#22c55e"
          : index === 0
          ? "#3b82f6"
          : "#d1d5db",
        color: isDarkMode ? "#f8fafc" : "#1e293b",
        borderRadius: "8px",
        padding: "10px",
        width: 150,
        fontWeight: "bold",
        textAlign: "center",
      },
    }));
  }, [config.states, isDarkMode]);

  // Map transitions to edges
  const initialEdges = useMemo(() => {
    const edgeCounts = {};

    return config.transitions.map((t, index) => {
      const pairId = `${t.from}-${t.to}`;
      edgeCounts[pairId] = (edgeCounts[pairId] || 0) + 1;
      const count = edgeCounts[pairId];

      let curvature = t.curvature !== undefined ? t.curvature : 0.25;
      if (t.curvature === undefined && count > 1) {
        curvature =
          0.25 + Math.floor(count / 2) * 0.2 * (count % 2 === 0 ? 1 : -1);
      }

      return {
        id: t.id || `e-${t.from}-${t.to}-${index}`,
        source: t.from,
        target: t.to,
        label: t.action,
        type: "curved",
        data: {
          curvature,
          transitionId: t.id,
          onCurvatureChange,
          onCurvatureChangeEnd,
        },
        animated: true,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: isDarkMode ? "#60a5fa" : "#3b82f6",
        },
        style: { stroke: isDarkMode ? "#60a5fa" : "#3b82f6" },
      };
    });
  }, [config.transitions, onCurvatureChange, onCurvatureChangeEnd, isDarkMode]);

  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges]);

  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  );

  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  );

  const onConnect = useCallback(
    (params) => {
      const newId = `t-${Date.now()}`;
      const newEdge = {
        ...params,
        id: newId,
        label: "Nuova Azione",
        type: "curved",
        data: {
          curvature: 0.25,
          transitionId: newId,
          onCurvatureChange,
          onCurvatureChangeEnd,
        },
        animated: true,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: isDarkMode ? "#60a5fa" : "#3b82f6",
        },
        style: { stroke: isDarkMode ? "#60a5fa" : "#3b82f6" },
      };

      const updatedEdges = addEdge(newEdge, edges);
      setEdges(updatedEdges);

      // Persist the new edge immediately so it doesn't vanish
      const newTransition = {
        id: newId,
        from: params.source,
        to: params.target,
        action: "Nuova Azione",
        requiredRoles: [],
        validationRules: [],
        endpoint: "",
        curvature: 0.25,
      };

      onUpdateConfig({
        ...config,
        transitions: [...config.transitions, newTransition],
      });

      setEditingEdge(newEdge);
      setTransitionData({
        action: "Nuova Azione",
        requiredRoles: [],
        validationRules: "",
        endpoint: "",
        curvature: 0.25,
      });
      setIsModalOpen(true);
    },
    [
      onCurvatureChange,
      onCurvatureChangeEnd,
      isDarkMode,
      edges,
      config,
      onUpdateConfig,
    ],
  );

  const onEdgeClick = useCallback(
    (event, edge) => {
      const transitionId = edge.data?.transitionId;
      const transition =
        config.transitions.find((t) => t.id === transitionId) ||
        config.transitions.find(
          (t) =>
            t.from === edge.source &&
            t.to === edge.target &&
            t.action === edge.label,
        );

      setEditingEdge(edge);
      setTransitionData({
        action: edge.label || "",
        requiredRoles: transition?.requiredRoles || [],
        validationRules: transition?.validationRules?.join(", ") || "",
        endpoint: transition?.endpoint || "",
        curvature: edge.data?.curvature || 0.25,
      });
      setIsModalOpen(true);
    },
    [config.transitions],
  );

  const handleSaveTransition = () => {
    if (!editingEdge) return;

    const updatedEdges = edges.map((e) => {
      if (e.id === editingEdge.id) {
        return {
          ...e,
          label: transitionData.action,
          data: { ...e.data, curvature: transitionData.curvature },
        };
      }
      return e;
    });

    setEdges(updatedEdges);
    setIsModalOpen(false);
    syncToConfig(updatedEdges, nodes);
  };

  const handleDeleteTransition = () => {
    if (!editingEdge) return;

    const updatedEdges = edges.filter((e) => e.id !== editingEdge.id);
    setEdges(updatedEdges);
    setIsModalOpen(false);
    syncToConfig(updatedEdges, nodes);
  };

  const handleManualSync = () => {
    syncToConfig(edges, nodes);
  };

  const onEdgesDelete = useCallback(
    (deletedEdges) => {
      const remainingEdges = edges.filter(
        (e) => !deletedEdges.find((de) => de.id === e.id),
      );
      syncToConfig(remainingEdges, nodes);
    },
    [edges, nodes, syncToConfig],
  );

  const onNodesDelete = useCallback(
    (deletedNodes) => {
      const remainingNodes = nodes.filter(
        (n) => !deletedNodes.find((dn) => dn.id === n.id),
      );
      const remainingEdges = edges.filter(
        (e) =>
          !deletedNodes.find((dn) => dn.id === e.source || dn.id === e.target),
      );
      syncToConfig(remainingEdges, remainingNodes);
    },
    [nodes, edges, syncToConfig],
  );

  return (
    <Box
      sx={{
        height: 600,
        width: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        position: "relative",
        "& .react-flow__controls-button": {
          backgroundColor: isDarkMode ? "#1e293b" : "#fff",
          color: isDarkMode ? "#f8fafc" : "#1e293b",
          borderBottom: `1px solid ${isDarkMode ? "#334155" : "#eee"}`,
          fill: isDarkMode ? "#f8fafc" : "#1e293b",
          "&:hover": {
            backgroundColor: isDarkMode ? "#334155" : "#f4f4f4",
          },
          "& svg": {
            fill: "currentColor",
          },
        },
        "& .react-flow__controls-button:last-child": {
          borderBottom: "none",
        },
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onEdgeClick={onEdgeClick}
        onEdgesDelete={onEdgesDelete}
        onNodesDelete={onNodesDelete}
        onNodeDragStop={onNodeDragStop}
        edgeTypes={edgeTypes}
        fitView
        style={{
          background: isDarkMode ? "#0f172a" : "#fafafa",
          color: isDarkMode ? "#f8fafc" : "#1e293b",
        }}
      >
        <Background color={isDarkMode ? "#334155" : "#ccc"} gap={20} />
        <Controls />
        <MiniMap
          style={{
            backgroundColor: isDarkMode ? "#1e293b" : "#fff",
            border: `1px solid ${isDarkMode ? "#334155" : "#ccc"}`,
          }}
          maskColor={
            isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"
          }
          nodeColor={isDarkMode ? "#475569" : "#d1d5db"}
        />
        <Panel position="top-right">
          <Paper
            elevation={3}
            sx={{
              p: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
              onClick={handleManualSync}
              color="primary"
            >
              Applica Modifiche
            </Button>
          </Paper>
        </Panel>
      </ReactFlow>

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SettingsIcon color="primary" />
          Configura Azione
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                p: 2,
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {editingEdge?.source}
              </Typography>
              <Typography variant="caption">→</Typography>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {editingEdge?.target}
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Nome Azione"
              value={transitionData.action}
              onChange={(e) =>
                setTransitionData({ ...transitionData, action: e.target.value })
              }
            />

            <FormControl fullWidth size="small" margin="none">
              <InputLabel id="ruoli-abilitati-label">Ruoli Abilitati</InputLabel>
              <Select
                labelId="ruoli-abilitati-label"
                label="Ruoli Abilitati"
                multiple
                value={transitionData.requiredRoles}
                onChange={(e) =>
                  setTransitionData({
                    ...transitionData,
                    requiredRoles:
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value,
                  })
                }
                input={<OutlinedInput label="Ruoli Abilitati" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {config.roles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Regole di Validazione (separate da virgola)"
              placeholder="es. check_auth, verify_data"
              value={transitionData.validationRules}
              onChange={(e) =>
                setTransitionData({
                  ...transitionData,
                  validationRules: e.target.value,
                })
              }
            />

            <TextField
              fullWidth
              label="Endpoint API"
              placeholder="es. /api/approve"
              value={transitionData.endpoint}
              onChange={(e) =>
                setTransitionData({
                  ...transitionData,
                  endpoint: e.target.value,
                })
              }
            />

            <Box>
              <Typography variant="caption" gutterBottom color="text.secondary">
                Regola Curvatura (Trascina l'etichetta nel grafico o usa il cursore)
              </Typography>
              <TextField
                fullWidth
                type="range"
                inputProps={{ min: -1, max: 1, step: 0.05 }}
                value={transitionData.curvature}
                onChange={(e) =>
                  setTransitionData({
                    ...transitionData,
                    curvature: parseFloat(e.target.value),
                  })
                }
                size="small"
              />
              <Typography variant="caption" align="center" display="block">
                {transitionData.curvature.toFixed(2)}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteTransition}
          >
            Elimina Azione
          </Button>
          <Box>
            <Button onClick={() => setIsModalOpen(false)} sx={{ mr: 1 }}>
              Annulla
            </Button>
            <Button variant="contained" onClick={handleSaveTransition}>
              Salva
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const WorkflowGraph = (props) => (
  <ReactFlowProvider>
    <WorkflowGraphInner {...props} />
  </ReactFlowProvider>
);

export default WorkflowGraph;
